import { apiFetch, parseApiResponse } from '../../utils/apiClient';
import { isQuoteRevision, requireQuoteRevision } from './quoteRevision';
import { readOptionsState } from './quoteOptionsState';

/* global globalThis */

const operationKey = () => globalThis.crypto?.randomUUID?.()
  || `quote-${Date.now()}-${Array.from(globalThis.crypto.getRandomValues(new Uint32Array(4))).map(n => n.toString(16)).join('')}`;

const versionMutation = async (url, options, revision) => {
  const expectedRevision = requireQuoteRevision(revision);
  const response = await apiFetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'If-Match': `"rev-${expectedRevision}"`,
      'Idempotency-Key': options.headers?.['Idempotency-Key'] || operationKey(),
    },
  });
  const data = await parseApiResponse(response);
  const nextRevision = response.headers.get('X-Quote-Version-Revision') ?? data?.revision;
  if (data && isQuoteRevision(nextRevision)) {
    Object.defineProperty(data, '_revision', { value: Number(nextRevision), enumerable: false });
  }
  return data;
};

export const getQuotes = async () => {
  const response = await apiFetch('/api/quotes');
  return parseApiResponse(response);
};

export const getQuotePermissions = async () =>
  parseApiResponse(await apiFetch('/api/quotes/permissions'));

export const getQuoteFolder = async (quoteId) =>
  parseApiResponse(await apiFetch(`/api/quotes/${quoteId}`));

export const getQuoteVersions = async (quoteId) =>
  parseApiResponse(await apiFetch(`/api/quotes/${quoteId}/versions`));

export const getQuoteDocuments = async (quoteId) =>
  parseApiResponse(await apiFetch(`/api/quotes/${quoteId}/documents`));

export const getQuoteHistory = async (quoteId) =>
  parseApiResponse(await apiFetch(`/api/quotes/${quoteId}/history`));

export const getQuoteCustomers = async () =>
  parseApiResponse(await apiFetch('/api/partners'));

export const getQuoteCustomer = async (companyId) =>
  parseApiResponse(await apiFetch(`/api/partners/${companyId}`));

export const proposeQuoteCustomerSnapshot = async (versionId, companyId, contactId = '') => {
  const query = new URLSearchParams({ companyId: String(companyId) });
  if (contactId) query.set('contactId', String(contactId));
  return parseApiResponse(await apiFetch(
    `/api/quote-versions/${versionId}/customer-snapshot?${query.toString()}`
  ));
};

export const updateQuoteVersionInformation = async (versionId, payload, revision) =>
  versionMutation(`/api/quote-versions/${versionId}/information`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }, revision);

export const updateQuoteVersionParameters = async (versionId, parameters, revision) =>
  versionMutation(`/api/quote-versions/${versionId}/parameters`, {
    method: 'PUT',
    body: JSON.stringify({ parameters }),
  }, revision);

export const updateQuoteVersionExpenses = async (versionId, expenses, revision) =>
  versionMutation(`/api/quote-versions/${versionId}/expenses`, {
    method: 'PUT',
    body: JSON.stringify({ expenses }),
  }, revision);

export const saveQuoteVersionDraft = async (versionId, payload, revision) =>
  versionMutation(`/api/quote-versions/${versionId}/draft`, {
    method: 'PUT',
    body: JSON.stringify({
      revision,
      folder: {
        title: payload.title,
        affairId: payload.affairId,
        projectId: payload.projectId,
        companyId: payload.companyId,
        quoteType: payload.quoteType,
      },
      information: payload.versionInformation || {},
      ...(Array.isArray(payload.versionParameters) ? { parameters: payload.versionParameters } : {}),
      ...(Array.isArray(payload.sections) ? { sections: payload.sections } : {}),
      replaceSections: payload.replaceSections === true,
      ...(Array.isArray(payload.versionExpenses) ? { expenses: payload.versionExpenses } : {}),
      ...(Array.isArray(payload.paymentSchedule) ? { paymentSchedule: payload.paymentSchedule } : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, 'options') ? { options: payload.options } : {}),
    }),
  }, revision);

export const getQuoteCalculation = async (versionId) =>
  parseApiResponse(await apiFetch(`/api/quote-versions/${versionId}/calculation`));

export const getQuoteMinute = async (versionId) =>
  parseApiResponse(await apiFetch(`/api/quote-versions/${versionId}/minute`));

export const previewQuoteDocument = async (quoteId, versionId, documentType) =>
  parseApiResponse(await apiFetch(
    `/api/quotes/${quoteId}/versions/${versionId}/documents/preview`,
    { method: 'POST', body: JSON.stringify({ documentType }) }
  ));

export const generateOfficialQuoteDocument = async (quoteId, versionId, documentType = 'client_quote') =>
  parseApiResponse(await apiFetch(
    `/api/quotes/${quoteId}/versions/${versionId}/documents/generate`,
    { method: 'POST', body: JSON.stringify({ documentType }) }
  ));

export const archiveQuoteDocumentToGed = async (quoteId, versionId, documentId, payload = {}) =>
  parseApiResponse(await apiFetch(
    `/api/quotes/${quoteId}/versions/${versionId}/documents/${documentId}/archive-ged`,
    { method: 'POST', body: JSON.stringify(payload) }
  ));

export const downloadQuoteDocument = async (quoteId, versionId, documentId) => {
  const response = await apiFetch(
    `/api/quotes/${quoteId}/versions/${versionId}/documents/${documentId}/download`
  );
  if (!response.ok) await parseApiResponse(response);
  return response.blob();
};

const mergeQuoteVersion = (folder, detail) => {
  const flattenSections = (sections = []) => sections.flatMap((section) => [
    { ...section, children: undefined },
    ...flattenSections(section.children || []),
  ]);
  return {
    ...folder,
    contentSchemaVersion: detail.contentSchemaVersion || 1,
    composition: detail.composition || null,
    clientDocumentV2: detail.clientDocumentV2 || null,
    quoteVersionId: detail.id,
    version: {
      ...detail.general,
      id: detail.id,
      versionNumber: detail.versionNumber,
      revision: detail.revision,
      versionLabel: detail.versionLabel,
      status: detail.status,
      createdAt: detail.createdAt,
    },
    customerSnapshot: detail.customerSnapshot,
    issuerSnapshot: detail.issuerSnapshot,
    parameters: detail.parameters,
    paymentSchedule: detail.paymentSchedule || [],
    sections: flattenSections(detail.sections),
    expenses: detail.expenses,
    options: Array.isArray(detail.options) ? detail.options : [],
    optionsOriginalState: readOptionsState(detail.options, detail.optionsState),
    totals: detail.totals,
    financialPresentation: detail.financialPresentation,
    clientDocument: detail.clientDocument,
    documents: detail.documents,
    permissions: detail.permissions,
  };
};

export const getQuoteVersion = async (quoteId, versionId, existingFolder = null) => {
  const folder = existingFolder || await getQuoteFolder(quoteId);
  const detail = await parseApiResponse(
    await apiFetch(`/api/quotes/${quoteId}/versions/${versionId}`)
  );
  return mergeQuoteVersion(folder, detail);
};

export const getQuote = async (quoteId) => {
  const folder = await getQuoteFolder(quoteId);
  const versionId = folder.currentVersionId || folder.current_version_id || folder.quoteVersionId;
  return versionId ? getQuoteVersion(quoteId, versionId, folder) : folder;
};

export const createQuote = async (payload) => {
  const response = await apiFetch('/api/quotes', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const folder = await parseApiResponse(response);
  const versionId = folder.currentVersionId || folder.quoteVersionId;
  const createdQuote = versionId ? await getQuote(folder.id) : folder;
  const revision = createdQuote.version?.revision ?? folder.currentVersion?.revision ?? 0;
  if (versionId) {
    await saveQuoteVersionDraft(versionId, {
      ...payload,
      // La création backend vient de capturer le référentiel. Le premier
      // enregistrement du contenu ne doit jamais remplacer ce snapshot par [].
      ...(createdQuote.permissions?.canViewCosts === false ? {} : { versionParameters: createdQuote.parameters || [] }),
    }, revision);
  }
  return getQuote(folder.id);
};

export const updateQuote = async (quoteId, quoteVersionId, payload) => {
  if (!quoteVersionId) throw new Error('La version du devis est obligatoire.');
  const result = await saveQuoteVersionDraft(quoteVersionId, payload, payload.revision);
  const quote = await getQuote(quoteId);
  if (isQuoteRevision(result?._revision)) {
    Object.defineProperty(quote, '_revision', { value: result._revision, enumerable: false });
  }
  return quote;
};

export const saveQuoteLineOrder = async (quoteId, versionId, sections, revision) => {
  const result = await versionMutation(`/api/quote-versions/${versionId}/lines/reorder`, {
    method: 'POST', body: JSON.stringify({ items: (sections || []).flatMap((section) =>
      (section.lines || []).map((line, orderIndex) => ({ id: line.id, orderIndex }))) }),
  }, revision);
  const quote = await getQuote(quoteId);
  if (isQuoteRevision(result?._revision)) Object.defineProperty(quote, '_revision', { value: result._revision, enumerable: false });
  return quote;
};

export const recalculateQuote = async (quoteId, quoteVersionId, ventilationMode, revision) => {
  if (!quoteVersionId) throw new Error('La version du devis est obligatoire.');
  return versionMutation(`/api/quote-versions/${quoteVersionId}/recalculate`, {
    method: 'POST',
    body: JSON.stringify({ ventilationMode }),
  }, revision);
};

export const previewQuote = async (payload) => {
  const response = await apiFetch('/api/quotes/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  return parseApiResponse(response);
};

const forceLineValue = async (quoteId, quoteVersionId, lineId, kind, payload, revision) =>
  versionMutation(
    `/api/quote-versions/${quoteVersionId}/lines/${lineId}/force-${kind}`,
    { method: 'POST', body: JSON.stringify(payload) }, revision
  );

export const forceLinePrice = (quoteId, quoteVersionId, lineId, payload, revision) =>
  forceLineValue(quoteId, quoteVersionId, lineId, 'price', payload, revision);

export const forceLineCoefficient = (quoteId, quoteVersionId, lineId, payload, revision) =>
  forceLineValue(quoteId, quoteVersionId, lineId, 'coefficient', payload, revision);

export const resetLineForcing = async (quoteId, quoteVersionId, lineId, target, revision) =>
  versionMutation(`/api/quote-versions/${quoteVersionId}/lines/${lineId}/reset-forcing`, {
    method: 'POST', body: JSON.stringify({ target }),
  }, revision);

export const runQuoteVersionCommand = async (quoteId, quoteVersionId, command, payload = {}, revision) => {
  const suffix = command === 'create' ? '' : `/${quoteVersionId}/${command}`;
  if (command !== 'create' || isQuoteRevision(revision)) return versionMutation(`/api/quotes/${quoteId}/versions${suffix}`, {
    method: 'POST', body: JSON.stringify(payload),
  }, revision);
  const response = await apiFetch(`/api/quotes/${quoteId}/versions${suffix}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return parseApiResponse(response);
};

// quote_engine V1 - opérations de calcul sans persistance.
export const calculateQuoteEngine = async (request) =>
  parseApiResponse(await apiFetch('/api/v1/quotes/calculate', {
    method: 'POST',
    body: JSON.stringify(request),
  }));

export const validateQuoteEngine = async (request) =>
  parseApiResponse(await apiFetch('/api/v1/quotes/validate', {
    method: 'POST',
    body: JSON.stringify(request),
  }));

export const createQuoteEngineSnapshot = async (request) =>
  parseApiResponse(await apiFetch('/api/v1/quotes/snapshots', {
    method: 'POST',
    body: JSON.stringify(request),
  }));

export const getQuoteCustomerTerms = async (companyId) =>
  parseApiResponse(await apiFetch(`/api/partners/${companyId}/quote-settings`));

export const mutateQuoteComposition = (versionId, mutation, revision, idempotencyKey = operationKey()) =>
  versionMutation(`/api/quote-versions/${versionId}/draft`, { method: 'PUT',
    headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ compositionMutation: mutation }) }, revision);

export const enableQuoteComposition = (versionId, revision) =>
  versionMutation(`/api/quote-versions/${versionId}/draft`, { method: 'PUT',
    body: JSON.stringify({ enableComposition: true, explicitUpgrade: true, confirmLegacyCosts: true }) }, revision);

export const saveCompositionInformation = (versionId, payload, revision) =>
  versionMutation(`/api/quote-versions/${versionId}/draft`, { method: 'PUT', body: JSON.stringify(payload) }, revision);

export const previewCompositionPdf = async (quoteId, versionId, documentType) => {
  const response = await apiFetch(`/api/quotes/${quoteId}/versions/${versionId}/documents/preview?format=pdf`, {
    method: 'POST', body: JSON.stringify({ documentType }),
  });
  if (!response.ok) await parseApiResponse(response);
  return response.blob();
};
